//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useContext, useCallback, useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import SendIcon from '@material-ui/icons/Send';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
// import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
// import IconButton from '@material-ui/core/IconButton';
// import ButtonGroup from '@material-ui/core/ButtonGroup';
// import Button from '@material-ui/core/Button';
// import Input from '@material-ui/core/Input';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { sendFileReply } from '../../common/services/myMessagesServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdTextField from '../../common/elemental/AfitdTextField';
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdLinearProgress from '../../common/elemental/AfitdLinearProgress';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import MessageFileBlock from './MessageFileBlock';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
// import _range from 'lodash/range';
import _get from 'lodash/get';
// import _set from 'lodash/set';
import _last from 'lodash/last';
import _some from 'lodash/some';
import _toUpper from 'lodash/toUpper';
import _isNil from 'lodash/isNil';
import _trim from 'lodash/trim';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => ({
        root: {},
        replyMessage: {
            flexGrow: 1,
            // minWidth: '400px',
            marginRight: '8px',
        },
        replyInputRoot: {
            '&$replyInputFocused $replyInputNotchedOutline': {
                borderColor: `${theme.palette.secondary.main} !important`,
            },
        },
        replyInputFocused: {},
        replyInputNotchedOutline: {
            borderWidth: '2px',
            borderColor: '#D5D7DD !important',
        },
        replyInput: {
            fontSize: '16px',
        },
        replyInputMultiline: {
            padding: '14px',
        },
        dialogContentContainer: {
            marginTop: '20px',
            // marginLeft: '10px',
        },
        selectedFileContainer: {
            marginTop: '6px',
        },
        uploadLinearProgressContainer: {
            marginTop: '20px',
        },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { classes, props, userId, currentThreadId, message, progress, isUploading } = params;
    const { setMessage, setProgress, setIsUploading, cleanUpDialog } = params;
    const {
        onAttachmentUploadComplete,
        onClose,
        selectedFile,
        allowedFileTypes,
        // message: messageProp,
        // setMessage: setMessageProp,
        onMessageSend,
        clearReply,
        fileInputRef,
        ...otherProps
    } = props;
    const {
        replyInputRoot: replyInputRootCls,
        replyInput: replyInputCls,
        replyInputFocused: replyInputFocusedCls,
        replyInputNotchedOutline: replyInputNotchedOutlineCls,
        replyInputMultiline: replyInputMultilineCls,
    } = classes;

    const selectedFileAttachment = {
        title: _get(selectedFile, 'name', ''),
        size: _get(selectedFile, 'size', ''),
        type: _get(selectedFile, 'type', ''),
    };

    let errorMessage = null;

    if (selectedFile) {
        if (!validateFileType(selectedFile, allowedFileTypes)) {
            errorMessage = 'The selected file is not allowed';
        } else if (!validateFileSize(selectedFile)) {
            errorMessage = 'The selected file may not exceed 10MB';
        }
    }

    const isValidFile = !!!errorMessage;

    const defns = {
        root: {
            ...otherProps,
        },
        replyMessage: {
            fullWidth: true,
            variant: 'outlined',
            // size: 'small',
            multiline: true,
            placeholder: 'Type your message...',
            InputProps: {
                // readOnly: !isEnabled,
                classes: {
                    root: replyInputRootCls,
                    focused: replyInputFocusedCls,
                    notchedOutline: replyInputNotchedOutlineCls,
                    input: replyInputCls,
                    multiline: replyInputMultilineCls,
                },
            },
            value: message,
            onKeyPress: handleKeyUpReplyMessage.bind(
                null,
                message,
                selectedFile,
                userId,
                currentThreadId,
                setProgress,
                setIsUploading,
                onAttachmentUploadComplete.bind(null, cleanUpDialog, message),
            ),
            onChange: handleChangeReplyMessage.bind(null, setMessage),
            // disabled,
        },
        selectedFile: {
            attachment: selectedFileAttachment,
            isClickable: false,
        },
        errorText: {
            clsRedColor: true,
            value: errorMessage,
            isHidden: isValidFile,
        },
        uploadLinearProgress: {
            variant: 'determinate',
            value: progress,
            isHidden: _isNil(progress),
        },
        cancelButton: {
            onClick: handleClickCancelButton.bind(null, cleanUpDialog),
        },
        sendButton: {
            color: 'secondary',
            onClick: handleClickSendButton.bind(
                null,
                selectedFile,
                userId,
                currentThreadId,
                setProgress,
                setIsUploading,
                onAttachmentUploadComplete.bind(null, cleanUpDialog, message),
            ),
            autoFocus: true,
            disabled: !selectedFile || !isValidFile || isUploading,
        },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const handleClickSendButton = (file, userId, threadId, setProgress, setIsUploading, onAttachmentUploadComplete) => {
    sendFileReply({ file, userId, threadId, setProgress, setIsUploading, onAttachmentUploadComplete });
};

const handleClickCancelButton = cleanUpDialog => {
    cleanUpDialog();
};

const validateFileType = (file, allowedFileTypes) => {
    const filename = _get(file, 'name', '');
    const extension = _toUpper(`.${_last(filename.split('.'))}`);
    const isTypeAllowed = _some(allowedFileTypes, item => _toUpper(item) === extension);
    return isTypeAllowed;
};

const validateFileSize = file => {
    const size = _get(file, 'size', 0);
    const isSizeAllowed = size <= 10000000; // 10MB
    return isSizeAllowed;
};

const handleChangeReplyMessage = (setMessage, evt) => {
    setMessage(evt.target.value);
};

const handleKeyUpReplyMessage = (message, file, userId, threadId, setProgress, setIsUploading, onAttachmentUploadComplete, evt) => {
    if (evt.key === 'Enter' && !evt.shiftKey) {
        evt.preventDefault();
        if (!_isNil(message) && _trim(message) !== '') {
            sendFileReply({ file, userId, threadId, setProgress, setIsUploading, onAttachmentUploadComplete });
        }
    }
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const FileReplyDialog = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();
    const context = useContext(LayoutContext);
    const appContext = useContext(AppContext);
    const userId = _get(context, 'userId');
    const draftReplies = _get(appContext, 'draftReplies');
    const currentThreadId = _get(context, 'currentThreadId');

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { onClose, fileInputRef, message: messageProp } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [message, setMessage] = useState();
    const [progress, setProgress] = useState(null);
    const [isUploading, setIsUploading] = useState(false);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback
    //---------------------------------------------------------------------
    const cleanUpDialog = useCallback(() => {
        onClose(); // fire onClose event
        setProgress(null); // reset progress bar
        setIsUploading(false); // reset isUploading flag
        setMessage(''); // clear reply text field
        fileInputRef.current.value = ''; // clear the file input
    }, [onClose, fileInputRef]);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        setMessage(messageProp);
    }, [messageProp]);

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const defns = getDefns({ classes, props, userId, message, setMessage, progress, setProgress, isUploading, setIsUploading, cleanUpDialog, draftReplies, currentThreadId });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <Dialog {...defns.root}>
            <DialogTitle>Send Attachment with Message</DialogTitle>
            <DialogContent>
                <div {...defns.dialogContentContainer}>
                    <AfitdTextField {...defns.replyMessage} />
                    <div {...defns.selectedFileContainer}>
                        <MessageFileBlock {...defns.selectedFile} />
                    </div>
                    <AfitdTypography {...defns.errorText} />
                    <div {...defns.uploadLinearProgressContainer}>
                        <AfitdLinearProgress {...defns.uploadLinearProgress} />
                    </div>
                </div>
            </DialogContent>
            <DialogActions>
                <AfitdButton startIcon={<HighlightOffIcon />} {...defns.cancelButton}>
                    Cancel
                </AfitdButton>
                <AfitdButton startIcon={<SendIcon />} {...defns.sendButton}>
                    Send
                </AfitdButton>
            </DialogActions>
        </Dialog>
    );
};

export default FileReplyDialog;
